import React, { useState } from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import RelationshipSelect from '../common/RelationshipSelect'
const { FiSearch, FiX, FiUserPlus, FiPlus, FiSave, FiUser } = FiIcons

const ADD_NEW_PERSON_OPTION = '__add_new_person__'

// Helper to capitalize words
const capitalizeWords = (str) => {
  if (!str) return ''
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const relationshipGroups = [
  {
    name: "PERSONAL",
    options: [
      { type: 'friend', label: 'Friend', group: 'Personal' }
    ]
  },
  {
    name: "FAMILY",
    options: [
      { type: 'parent', label: 'Parent (Father/Mother)', group: '1st Generation' },
      { type: 'child', label: 'Child (Son/Daughter)', group: '1st Generation' },
      { type: 'sibling', label: 'Sibling (Brother/Sister)', group: '1st Generation' },
      { type: 'spouse', label: 'Spouse (Husband/Wife)', group: '1st Generation' },
      { type: 'ex_spouse', label: 'Ex-Spouse (Ex-Husband/Ex-Wife)', group: '1st Generation' },
      { type: 'partner', label: 'Partner', group: '1st Generation' },
      { type: 'step_parent', label: 'Step-parent (Stepfather/Stepmother)', group: '1st Generation' },
      { type: 'step_child', label: 'Step-child (Stepson/Stepdaughter)', group: '1st Generation' },
      { type: 'step_sibling', label: 'Step-sibling (Stepbrother/Stepsister)', group: '1st Generation' },
      { type: 'half_sibling', label: 'Half-sibling (Half-brother/Half-sister)', group: '1st Generation' },
      { type: 'adoptive_parent', label: 'Adoptive Parent', group: '1st Generation' },
      { type: 'adoptive_child', label: 'Adopted Child', group: '1st Generation' },
      { type: 'grandparent', label: 'Grandparent (Grandfather/Grandmother)', group: '2nd Generation' },
      { type: 'grandchild', label: 'Grandchild (Grandson/Granddaughter)', group: '2nd Generation' },
      { type: 'parental_sibling', label: 'Parental Sibling (Aunt/Uncle)', group: '2nd Generation' },
      { type: 'sibling_child', label: 'Sibling\'s Child (Niece/Nephew)', group: '2nd Generation' },
      { type: 'great_grandparent', label: 'Great-Grandparent', group: '2nd Generation' },
      { type: 'great_grandchild', label: 'Great-Grandchild', group: '2nd Generation' },
      { type: 'great_great_grandparent', label: 'Great-Great-Grandparent', group: '3rd Generation+' },
      { type: 'grandparent_sibling', label: 'Grandparent\'s Sibling (Great-aunt/Great-uncle)', group: '3rd Generation+' },
      { type: 'grandsibling_child', label: 'Grand-sibling\'s Child (Grandniece/Grandnephew)', group: '3rd Generation+' },
      { type: 'cousin', label: 'Cousin (1st Cousin)', group: '3rd Generation+' },
      { type: 'second_cousin', label: 'Second Cousin', group: '3rd Generation+' },
      { type: 'third_cousin', label: 'Third Cousin', group: '3rd Generation+' },
      { type: 'first_cousin_once_removed', label: 'First Cousin Once Removed', group: '3rd Generation+' },
      { type: 'first_cousin_twice_removed', label: 'First Cousin Twice Removed', group: '3rd Generation+' },
      { type: 'second_cousin_once_removed', label: 'Second Cousin Once Removed', group: '3rd Generation+' },
      { type: 'sibling_in_law', label: 'Sibling-in-law (Brother-in-law/Sister-in-law)', group: 'In-Laws' },
      { type: 'parent_in_law', label: 'Parent-in-law (Father-in-law/Mother-in-law)', group: 'In-Laws' },
      { type: 'child_in_law', label: 'Child-in-law (Son-in-law/Daughter-in-law)', group: 'In-Laws' },
      { type: 'cousin_in_law', label: 'Cousin-in-law', group: 'In-Laws' },
      { type: 'sibling_child_in_law', label: 'Niece-in-law / Nephew-in-law', group: 'In-Laws' },
      { type: 'parental_sibling_in_law', label: 'Aunt-in-law / Uncle-in-law', group: 'In-Laws' },
    ]
  },
  {
    name: "PROFESSIONAL",
    options: [
      { type: 'colleague', label: 'Colleague', group: 'Professional' },
      { type: 'boss', label: 'Boss', group: 'Professional' },
      { type: 'employee', label: 'Employee', group: 'Professional' }
    ]
  }
]

const RelationshipForm = ({ person, people, existingRelationships, allRelationships, onSubmit, onCancel, onAddNewPerson }) => {
  const [relatedPersonId, setRelatedPersonId] = useState('')
  const [relationshipType, setRelationshipType] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [isAddingNewPerson, setIsAddingNewPerson] = useState(false)
  const [newPersonName, setNewPersonName] = useState('')
  const [newPersonSex, setNewPersonSex] = useState('male')
  const [creatingPerson, setCreatingPerson] = useState(false)

  const getAvailablePeople = () => {
    if (!people) return []
    const existingRelIds = new Set(
      (existingRelationships || []).map(rel => rel.person_a_id === person.id ? rel.person_b_id : rel.person_a_id)
    )
    return people.filter(p => p.id !== person.id && !existingRelIds.has(p.id))
  }

  const availablePeople = getAvailablePeople()

  const handleSearch = (query) => {
    setSearchTerm(query)
    if (!query.trim()) {
      setSearchResults([])
      return
    }
    const filtered = availablePeople.filter(p =>
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      (p.nicknames && p.nicknames.toLowerCase().includes(query.toLowerCase()))
    )
    setSearchResults(filtered)
  }

  const handleSelectPerson = (personId) => {
    setRelatedPersonId(personId)
    setSearchTerm('')
    setSearchResults([])
  }

  const handleAddNewPersonInline = async () => {
    if (!newPersonName.trim() || !onAddNewPerson) return
    setCreatingPerson(true)
    try {
      const newPerson = await onAddNewPerson(capitalizeWords(newPersonName.trim()), newPersonSex)
      if (newPerson?.id) {
        setRelatedPersonId(newPerson.id)
        setIsAddingNewPerson(false)
        setNewPersonName('')
        setNewPersonSex('male')
      }
    } catch (error) {
      console.error('Failed to add new person:', error)
    } finally {
      setCreatingPerson(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await onSubmit([{ relatedPersonId: relatedPersonId, relationshipType: relationshipType }])
    } catch (error) {
      console.error("Failed to add relationship:", error)
    } finally {
      setLoading(false)
    }
  }

  const getPersonRelationships = (personId) => {
    if (!allRelationships) return []
    return allRelationships
      .filter(rel => rel.person_a_id === personId || rel.person_b_id === personId)
      .map(rel => {
        const isPersonA = rel.person_a_id === personId
        const otherPersonId = isPersonA ? rel.person_b_id : rel.person_a_id
        const otherPerson = people.find(p => p.id === otherPersonId)
        if (!otherPerson) return null
        const relationshipType = isPersonA ? rel.relationship_type_b : rel.relationship_type
        return { personName: otherPerson.name, relationshipType: relationshipType || 'unknown' }
      })
      .filter(Boolean)
      .slice(0, 2)
  }

  const formatRelationshipInfo = (relationships) => {
    if (!relationships || relationships.length === 0) return ''
    return relationships.map(rel => `${rel.personName}(${rel.relationshipType})`).join(',')
  }

  const selectedPerson = people.find(p => p.id === relatedPersonId)

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
    >
      <h3 className="text-lg font-bold text-gray-800 mb-4">Add Relationship for {person.name}</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Person</label>
          
          {!isAddingNewPerson ? (
            <>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiSearch} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search people..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                {(searchTerm ? searchResults : availablePeople).map(p => {
                  const relationships = getPersonRelationships(p.id)
                  const relationshipInfo = formatRelationshipInfo(relationships)
                  return (
                    <div
                      key={p.id}
                      className={`p-3 cursor-pointer hover:bg-blue-50 ${relatedPersonId === p.id ? 'bg-blue-100' : ''}`}
                      onClick={() => handleSelectPerson(p.id)}
                    >
                      <div className="font-medium truncate">{p.name}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {relationshipInfo && (
                          <div className="text-xs text-purple-800 bg-purple-100 px-2 py-0.5 rounded-full whitespace-nowrap truncate">
                            {relationshipInfo}
                          </div>
                        )}
                        {p.nicknames && (
                          <div className="text-xs text-gray-500 italic truncate italic">
                            ({p.nicknames})
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
                
                {searchTerm && searchResults.length === 0 && (
                  <div className="p-3 text-center text-gray-500">
                    <p>No results for "{searchTerm}"</p>
                  </div>
                )}
                
                {onAddNewPerson && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddingNewPerson(true)
                      setNewPersonName(searchTerm.trim())
                    }}
                    className="w-full text-left px-3 py-3 bg-green-50 text-green-800 hover:bg-green-100 border-t border-green-200 flex items-center space-x-2 transition-colors"
                  >
                    <SafeIcon icon={FiPlus} className="text-green-600" />
                    <span>Add "{capitalizeWords(searchTerm || 'New Person')}" as a new person</span>
                  </button>
                )}
              </div>
              
              {selectedPerson && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-800">Selected: {selectedPerson.name}</span>
                  <button
                    type="button"
                    onClick={() => setRelatedPersonId('')}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <SafeIcon icon={FiX} size={16} />
                  </button>
                </div>
              )}
              
              {!searchTerm && onAddNewPerson && !selectedPerson && (
                <button
                  type="button"
                  onClick={() => setIsAddingNewPerson(true)}
                  className="mt-2 w-full py-2 px-4 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg border border-gray-300 flex items-center justify-center space-x-2 transition-colors"
                >
                  <SafeIcon icon={FiUserPlus} />
                  <span>Create New Person</span>
                </button>
              )}
            </>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center space-x-2 mb-2">
                <SafeIcon icon={FiUser} className="text-green-600" />
                <span className="font-medium text-green-800">Create New Person</span>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
                <input
                  type="text"
                  value={newPersonName}
                  onChange={(e) => setNewPersonName(e.target.value)}
                  placeholder="Enter name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Sex</label>
                <div className="flex space-x-2">
                  {['male', 'female', 'other'].map((sex) => (
                    <button
                      key={sex}
                      type="button"
                      onClick={() => setNewPersonSex(sex)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium capitalize transition-colors ${
                        newPersonSex === sex
                          ? 'bg-green-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {sex}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingNewPerson(false)
                    setNewPersonName('')
                    setNewPersonSex('male')
                  }}
                  className="flex-1 py-2 px-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddNewPersonInline}
                  disabled={!newPersonName.trim() || creatingPerson}
                  className="flex-1 py-2 px-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center justify-center space-x-2"
                >
                  {creatingPerson ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <SafeIcon icon={FiSave} size={14} />
                      <span>Create & Select</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Is their...</label>
          <RelationshipSelect
            value={relationshipType}
            onChange={setRelationshipType}
            relationshipGroups={relationshipGroups}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!relatedPersonId || !relationshipType || loading || isAddingNewPerson}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Adding...' : 'Add Relationship'}
          </button>
        </div>
      </form>
    </motion.div>
  )
}

export default RelationshipForm